import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useRef, useState } from "react";
import type { ApprovalTemplate } from "../api/generated";
import { hasPermission } from "../entities/access";
import { ApprovalTemplateForm } from "./ApprovalTemplateForm";
import {
  approvalTemplateError,
  approvalTemplateKeys,
  templatePatch,
} from "./approvalsSupport";
import { shellBootstrapQuery } from "./bootstrap";
import { formatDate, useSubmissionKey } from "./counterpartiesSupport";
import { Notice } from "./Notice";
import { Pagination } from "./Pagination";
import { Select } from "./Select";
import type { ShellServices } from "./services";
import { ErrorPage } from "./states";
import "./approvals.css";

const types = {
  incoming: "Входящий",
  outgoing: "Исходящий",
  internal: "Внутренний",
} as const;

function TemplateEditor({
  id,
  services,
  onClose,
}: {
  id: string;
  services: ShellServices;
  onClose: () => void;
}) {
  const detail = useQuery({
    queryKey: [...approvalTemplateKeys, "detail", id],
    queryFn: () => services.approvals!.template(id),
    retry: false,
    staleTime: 0,
  });
  if (detail.isLoading) return <p aria-busy="true">Загружаем шаблон…</p>;
  if (detail.isError)
    return (
      <Notice>
        {approvalTemplateError(detail.error)}{" "}
        <button type="button" onClick={() => void detail.refetch()}>
          Повторить
        </button>
        <button type="button" onClick={onClose}>
          Назад
        </button>
      </Notice>
    );
  return detail.data ? (
    <ApprovalTemplateForm
      template={detail.data}
      services={services}
      onSaved={onClose}
      onCancel={onClose}
    />
  ) : null;
}

function TemplatesDirectory({
  services,
  canManage,
}: {
  services: ShellServices;
  canManage: boolean;
}) {
  const bootstrap = useQuery(shellBootstrapQuery(services)).data;
  const context = bootstrap?.allOrganizations
    ? "all"
    : bootstrap?.currentScope?.id;
  const client = useQueryClient();
  const [page, setPage] = useState(1);
  const [active, setActive] = useState(false);
  const [type, setType] = useState<ApprovalTemplate["document_type"]>(null);
  const [editor, setEditor] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<ApprovalTemplate | null>(
    null,
  );
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);
  const sending = useRef(false);
  const key = useSubmissionKey();
  const list = useQuery({
    queryKey: [...approvalTemplateKeys, "list", context, page, active, type],
    queryFn: () =>
      services.approvals!.templates({
        page,
        per_page: 10,
        ...(active ? { is_active: true } : {}),
        ...(type === null ? {} : { document_type: type }),
      }),
    placeholderData: keepPreviousData,
    retry: false,
  });
  async function toggle() {
    if (sending.current || confirmation === null) return;
    sending.current = true;
    setPending(true);
    setError(null);
    try {
      // Перед заменой уровней перечитываем карточку: список мог устареть.
      const latest = await services.approvals!.template(confirmation.id);
      await services.approvals!.updateTemplate(
        latest.id,
        { ...templatePatch(latest), is_active: !confirmation.is_active },
        key.current(),
      );
      setPending(false);
      key.reset();
      await client.invalidateQueries({ queryKey: approvalTemplateKeys });
      setConfirmation(null);
    } catch (failure) {
      setPending(false);
      setError(failure);
    } finally {
      sending.current = false;
    }
  }
  return (
    <section
      className="workspace-page approval-page"
      aria-labelledby="approval-templates-title"
    >
      <div className="dw-heading">
        <h1 id="approval-templates-title">Шаблоны согласования</h1>
        {canManage ? (
          <button
            type="button"
            className="button button--primary"
            onClick={() => setEditor("new")}
          >
            Создать шаблон
          </button>
        ) : null}
      </div>
      <div className="approval-filters">
        <label className="cp-check">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => {
              setActive(event.target.checked);
              setPage(1);
            }}
          />
          <span>Только включённые</span>
        </label>
        <label className="field">
          <span>Тип документа</span>
          <Select
            value={type ?? ""}
            onChange={(event) => {
              const value = event.target.value;
              setType(
                value === "incoming" ||
                  value === "outgoing" ||
                  value === "internal"
                  ? value
                  : null,
              );
              setPage(1);
            }}
          >
            <option value="">Все типы</option>
            {Object.entries(types).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </label>
      </div>
      {list.isLoading ? <p aria-busy="true">Загружаем шаблоны…</p> : null}
      {list.isError ? (
        <Notice>
          Не удалось загрузить шаблоны.{" "}
          <button type="button" onClick={() => void list.refetch()}>
            Повторить
          </button>
        </Notice>
      ) : null}
      {list.isSuccess && list.data.data.length === 0 ? (
        <p>
          {active || type
            ? "Нет шаблонов с выбранными фильтрами"
            : "Шаблонов пока нет. Создайте первый, чтобы запускать согласование в один клик"}
        </p>
      ) : null}
      {list.data && list.data.data.length > 0 ? (
        <>
          <div className="approval-table-wrap">
            <table className="approval-table">
              <thead>
                <tr>
                  <th>Название</th>
                  {bootstrap?.allOrganizations ? <th>Организация</th> : null}
                  <th>Применяется к</th>
                  <th>Уровней</th>
                  <th>Статус</th>
                  <th>Дата изменения</th>
                  {canManage ? <th>Действия</th> : null}
                </tr>
              </thead>
              <tbody>
                {list.data.data.map((item) => (
                  <tr key={item.id}>
                    <td data-label="Название">{item.name}</td>
                    {bootstrap?.allOrganizations ? (
                      <td data-label="Организация">{item.access_scope.name}</td>
                    ) : null}
                    <td data-label="Применяется к">
                      {item.document_type
                        ? types[item.document_type]
                        : "Любой документ"}
                      {item.internal_kind
                        ? ` · ${item.internal_kind === "order" ? "Приказ" : "Служебная записка"}`
                        : ""}
                    </td>
                    <td data-label="Уровней">{item.steps.length}</td>
                    <td data-label="Статус">
                      <span className="approval-badge">
                        {item.is_active ? "Включён" : "Выключен"}
                      </span>
                    </td>
                    <td data-label="Дата изменения">
                      {formatDate(item.updated_at)}
                    </td>
                    {canManage ? (
                      <td data-label="Действия">
                        <div className="approval-actions">
                          <button
                            type="button"
                            className="button button--text"
                            onClick={() => setEditor(item.id)}
                          >
                            Изменить
                          </button>
                          <button
                            type="button"
                            className="button button--text"
                            onClick={() => {
                              key.reset();
                              setError(null);
                              setConfirmation(item);
                            }}
                          >
                            {item.is_active ? "Выключить" : "Включить"}
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={list.data.meta.current_page}
            lastPage={list.data.meta.last_page}
            total={list.data.meta.total}
            perPage={10}
            label="Страницы шаблонов согласования"
            busy={list.isFetching}
            onChange={setPage}
          />
        </>
      ) : null}
      {confirmation && canManage ? (
        <section
          className="cp-card"
          role="region"
          aria-label="Подтверждение изменения шаблона"
        >
          <h2>
            {confirmation.is_active ? "Выключить" : "Включить"} «
            {confirmation.name}»?
          </h2>
          <p>Запущенные согласования продолжатся по прежней схеме</p>
          {error ? <Notice>{approvalTemplateError(error)}</Notice> : null}
          <div className="approval-actions">
            <button
              className="button button--primary"
              type="button"
              disabled={pending}
              onClick={() => void toggle()}
            >
              Подтвердить
            </button>
            <button
              className="button button--secondary"
              type="button"
              disabled={pending}
              onClick={() => setConfirmation(null)}
            >
              Отмена
            </button>
          </div>
        </section>
      ) : null}
      {editor !== null && canManage ? (
        editor === "new" ? (
          <ApprovalTemplateForm
            key="new"
            services={services}
            onSaved={() => setEditor(null)}
            onCancel={() => setEditor(null)}
          />
        ) : (
          <TemplateEditor
            key={editor}
            id={editor}
            services={services}
            onClose={() => setEditor(null)}
          />
        )
      ) : null}
    </section>
  );
}

export function ApprovalTemplatesPage({
  services,
}: {
  services: ShellServices;
}) {
  const bootstrap = useQuery(shellBootstrapQuery(services));
  if (bootstrap.isLoading) return <p aria-busy="true">Загрузка…</p>;
  const permissions = bootstrap.data?.permissions ?? [];
  if (!hasPermission({ permissions }, "document-workflow.approvals.view"))
    return <ErrorPage status={403} />;
  if (!services.approvals) return <ErrorPage status={503} />;
  return (
    <TemplatesDirectory
      key={
        bootstrap.data?.allOrganizations
          ? "all"
          : (bootstrap.data?.currentScope?.id ?? "none")
      }
      services={services}
      canManage={hasPermission(
        { permissions },
        "document-workflow.approvals.templates.manage",
      )}
    />
  );
}
